/**
 * Arabic-first i18n with direction switching — CLAUDE.md §8.
 *
 * Arabic is the default and the fallback, not an added locale. That ordering is the
 * point: when a key is missing, the app falls back to Arabic rather than to English,
 * so an untranslated string is wrong for the smallest audience rather than for the
 * primary one.
 *
 * Digits stay Latin (`nu-latn`) even in Arabic. Order references, COD amounts and
 * barcodes are read aloud between a Courier and a dispatcher and typed into a search
 * box; Eastern Arabic numerals would make copying a reference off a parcel harder, not
 * easier. This is a deliberate choice, not an oversight.
 *
 * ## Arabic is bundled; English and French are fetched (slice 8, F20)
 *
 * All three were static imports, so every reader downloaded **515KB of JSON for 2,153 keys
 * each** and used a third of it. Arabic stays static because it is two things at once: the
 * default language *and* `fallbackLng`, so it must be in hand before the first paint and is
 * consulted for any key another bundle lacks. The other two arrive as their own chunk through
 * the backend below.
 *
 * Three properties were measured against i18next 24.2.3 before relying on them, because all
 * three are load-bearing and none is obvious from the option names:
 *
 * 1. **The backend is never asked for Arabic.** `partialBundledLanguages` tells i18next that
 *    `resources` is incomplete rather than complete, so it consults the backend for what is
 *    missing and *only* for that. An Arabic reader makes no extra request at all.
 * 2. **`changeLanguage` awaits the load.** It resolves after `read` calls back, so
 *    `await i18n.changeLanguage('en')` — which about twenty test files already write — needs no
 *    change and cannot observe a half-loaded language.
 * 3. **A failed load falls back to Arabic, not to raw keys.** A blocked or truncated chunk
 *    renders Arabic, which §8 makes the floor; the alternative failure mode is a screen of
 *    `common.loading`-shaped identifiers, which this repository has shipped once already.
 *
 * No new dependency. `i18next-resources-to-backend` does exactly this in a package, and the
 * backend contract it implements is one method — so it is fifteen lines here instead, which also
 * keeps the refusal of an unknown language ours to word.
 */

import i18next, { type BackendModule, type ReadCallback } from 'i18next';
import { initReactI18next } from 'react-i18next';
import ar from './ar.json';

export const LOCALES = ['ar', 'en', 'fr'] as const;
export type Locale = (typeof LOCALES)[number];

/** The languages that are fetched rather than bundled. Arabic is absent on purpose — see above. */
const FETCHED = {
  en: () => import('./en.json'),
  fr: () => import('./fr.json'),
} as const satisfies Partial<Record<Locale, () => Promise<{ default: Record<string, string> }>>>;

type FetchedLocale = keyof typeof FETCHED;

function isFetched(locale: string): locale is FetchedLocale {
  return Object.hasOwn(FETCHED, locale);
}

/**
 * One chunk per fetched language, named by a literal so Vite can see it.
 *
 * `import(./${lng}.json)` would also work and would emit a chunk for every JSON file the glob
 * matches, Arabic included — which is the one file that must stay in the main bundle.
 */
const localeBackend: BackendModule = {
  type: 'backend',
  init: () => undefined,
  read(language: string, _namespace: string, callback: ReadCallback): void {
    if (!isFetched(language)) {
      // `false` is i18next's "do not retry": a language we do not ship will not appear on a second
      // attempt, and retrying would hold `changeLanguage` open for a language nobody can reach.
      callback(new Error(`no bundle for '${language}'`), false);
      return;
    }
    void FETCHED[language]().then(
      (bundle) => callback(null, bundle.default),
      // `true` *is* a retry: this one is a network or integrity failure, which a second attempt
      // can legitimately survive. Until it does, i18next serves Arabic.
      (cause: unknown) => callback(cause instanceof Error ? cause : new Error(String(cause)), true),
    );
  },
};

const RTL_LOCALES: readonly Locale[] = ['ar'];

export function directionFor(locale: Locale): 'rtl' | 'ltr' {
  return RTL_LOCALES.includes(locale) ? 'rtl' : 'ltr';
}

/** Reflect the locale on <html>, so CSS logical properties resolve correctly. */
export function applyDirection(locale: Locale): void {
  const root = document.documentElement;
  root.lang = locale;
  root.dir = directionFor(locale);
}

export function initI18n(): typeof i18next {
  void i18next
    .use(initReactI18next)
    .use(localeBackend)
    .init({
      // Arabic only. The rest is `localeBackend`'s, which `partialBundledLanguages` is what permits.
      resources: { ar: { translation: ar } },
      partialBundledLanguages: true,
      lng: 'ar',
      fallbackLng: 'ar',
      interpolation: { escapeValue: false },
    });

  i18next.on('languageChanged', (lng) => {
    applyDirection(lng as Locale);
  });

  return i18next;
}

export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(`${locale}-u-nu-latn`).format(value);
}
